import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loader from '../common/Loader';

const formatDate = (val) => {
  if (val == null) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
};

const PAGE_SIZES = [10, 20, 50, 100];

const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total]);
  for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) pages.add(i);
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(`gap-${i}`);
    result.push(sorted[i]);
  }
  return result;
};

const PageFooter = ({ page, pageSize, total, totalPages, onPageChange, onPageSizeChange }) => (
  <div className="table-footer">
    <div className="rows-per-page"><span>Rows per page:</span><select value={pageSize} onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}>{PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
    <span className="pagination-info">{total === 0 ? 'No results' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
    {totalPages > 1 && (
      <div className="pagination">
        <button className="page-btn page-nav" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}><ChevronLeftIcon sx={{ fontSize: '1.25rem' }} /></button>
        {getPageNumbers(page, totalPages).map((p) => typeof p === 'string' ? <span key={p} className="page-ellipsis">…</span> : <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPageChange(p)}>{p}</button>)}
        <button className="page-btn page-nav" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}><ChevronRightIcon sx={{ fontSize: '1.25rem' }} /></button>
      </div>
    )}
  </div>
);

const GroupsTab = ({ orgId, apiCall, apiCallNoBody }) => {
  const base = `/admin/organizations/${orgId}`;

  // Groups list (paginated + server-side search)
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupsSearch, setGroupsSearch] = useState('');
  const [debouncedGroupsSearch, setDebouncedGroupsSearch] = useState('');

  // Create/edit group modal
  const [showModal, setShowModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [saving, setSaving] = useState(false);

  // Delete group
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Multi-select group members (inside modal)
  const [selectedGmIds, setSelectedGmIds] = useState(new Set());
  const [bulkRemoveGmConfirm, setBulkRemoveGmConfirm] = useState(false);
  const [bulkRemoveGmLoading, setBulkRemoveGmLoading] = useState(false);

  // ======= Group Members Modal =======
  const [gmGroup, setGmGroup] = useState(null); // active group object
  // Current group members (paginated)
  const [gmMembers, setGmMembers] = useState([]);
  const [gmPage, setGmPage] = useState(1);
  const [gmPageSize, setGmPageSize] = useState(20);
  const [gmTotal, setGmTotal] = useState(0);
  const [gmTotalPages, setGmTotalPages] = useState(1);
  const [gmLoading, setGmLoading] = useState(false);
  const [gmMemberIds, setGmMemberIds] = useState(new Set()); // IDs already in group for quick lookup
  const [gmSearch, setGmSearch] = useState('');
  const [debouncedGmSearch, setDebouncedGmSearch] = useState('');

  // Org members for picker (paginated)
  const [orgMembers, setOrgMembers] = useState([]);
  const [omPage, setOmPage] = useState(1);
  const [omPageSize, setOmPageSize] = useState(20);
  const [omTotal, setOmTotal] = useState(0);
  const [omTotalPages, setOmTotalPages] = useState(1);
  const [omLoading, setOmLoading] = useState(false);
  const [omSearch, setOmSearch] = useState('');
  const [debouncedOmSearch, setDebouncedOmSearch] = useState('');

  // Multi-select for adding members to group
  const [selectedToAdd, setSelectedToAdd] = useState(new Set());
  const [addingMembers, setAddingMembers] = useState(false);

  // Remove from group
  const [removeGmId, setRemoveGmId] = useState(null);
  const [removeGmLoading, setRemoveGmLoading] = useState(false);

  // ======= Fetch groups =======

  const fetchGroups = async (p = page, s = pageSize, q = debouncedGroupsSearch) => {
    setLoading(true); setError('');
    try {
      let url = `${base}/groups?page=${p}&page_size=${s}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      setGroups(data.groups || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Debounce groups search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedGroupsSearch(groupsSearch); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [groupsSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGroups(page, pageSize, debouncedGroupsSearch); }, [page, pageSize, debouncedGroupsSearch]);

  // ======= Group CRUD =======

  const openCreate = () => { setEditGroup(null); setForm({ name: '' }); setShowModal(true); };
  const openEdit = (g) => { setEditGroup(g); setForm({ name: g.name || '' }); setShowModal(true); };
  const closeModal = () => { if (saving) return; setShowModal(false); setEditGroup(null); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      if (editGroup) await apiCall(`${base}/groups/${editGroup.id}`, { method: 'PUT', body: JSON.stringify({ name: form.name }) });
      else await apiCall(`${base}/groups`, { method: 'POST', body: JSON.stringify({ name: form.name }) });
      setShowModal(false); setEditGroup(null);
      await fetchGroups(page, pageSize);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  const handleDelete = async (groupId) => {
    setDeleteLoading(true); setError('');
    try { await apiCallNoBody(`${base}/groups/${groupId}`, { method: 'DELETE' }); setDeleteId(null); await fetchGroups(page, pageSize); }
    catch (err) { setError(err.message); }
    setDeleteLoading(false);
  };

  // ======= Group Members Modal =======

  const fetchGroupMembers = async (groupId, p = 1, s = 20, q = debouncedGmSearch) => {
    setGmLoading(true);
    try {
      let url = `${base}/groups/${groupId}/members?page=${p}&page_size=${s}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      const members = data.members || [];
      setGmMembers(members);
      setGmTotal(data.total || 0);
      setGmTotalPages(data.total_pages || 1);
      setGmMemberIds(new Set(members.map((m) => m.user_id || m.id)));
    } catch (err) { setError(err.message); }
    setGmLoading(false);
  };

  const fetchOrgMembers = async (p = 1, s = 20, q = debouncedOmSearch) => {
    setOmLoading(true);
    try {
      let url = `${base}/members?page=${p}&page_size=${s}`;
      if (q && q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      const data = await apiCall(url);
      setOrgMembers(data.members || []);
      setOmTotal(data.total || 0);
      setOmTotalPages(data.total_pages || 1);
    } catch (err) { setError(err.message); }
    setOmLoading(false);
  };

  const openGroupMembers = async (group) => {
    setGmGroup(group);
    setGmPage(1); setGmMembers([]); setRemoveGmId(null); setSelectedGmIds(new Set());
    setGmSearch(''); setDebouncedGmSearch('');
    setOmPage(1); setOrgMembers([]); setOmSearch(''); setDebouncedOmSearch(''); setSelectedToAdd(new Set());
    await Promise.all([fetchGroupMembers(group.id, 1, gmPageSize, ''), fetchOrgMembers(1, omPageSize, '')]);
  };

  const closeGroupMembers = () => {
    setGmGroup(null); setRemoveGmId(null); setSelectedToAdd(new Set()); setSelectedGmIds(new Set()); setBulkRemoveGmConfirm(false);
  };

  // Debounce group members search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedGmSearch(gmSearch); setGmPage(1); }, 300);
    return () => clearTimeout(t);
  }, [gmSearch]);

  // Debounce org members picker search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedOmSearch(omSearch); setOmPage(1); }, 300);
    return () => clearTimeout(t);
  }, [omSearch]);

  // Re-fetch on page/search change inside modal
  useEffect(() => {
    if (gmGroup) fetchGroupMembers(gmGroup.id, gmPage, gmPageSize, debouncedGmSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmPage, gmPageSize, debouncedGmSearch]);

  useEffect(() => {
    if (gmGroup) fetchOrgMembers(omPage, omPageSize, debouncedOmSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omPage, omPageSize, debouncedOmSearch]);

  // Toggle selection for adding
  const toggleAddSelect = (userId) => {
    setSelectedToAdd((prev) => { const next = new Set(prev); if (next.has(userId)) next.delete(userId); else next.add(userId); return next; });
  };

  const handleAddSelectedToGroup = async () => {
    if (!gmGroup || selectedToAdd.size === 0) return;
    setAddingMembers(true); setError('');
    try {
      const results = await Promise.allSettled([...selectedToAdd].map((uid) =>
        apiCall(`${base}/groups/${gmGroup.id}/members`, { method: 'POST', body: JSON.stringify({ user_id: uid }) })
      ));
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0 && failures.length < selectedToAdd.size) {
        setError(`${selectedToAdd.size - failures.length} added, ${failures.length} failed (may already be in group).`);
      } else if (failures.length === selectedToAdd.size) {
        setError('Failed to add members. They may already be in the group.');
      }
      setSelectedToAdd(new Set());
      await fetchGroupMembers(gmGroup.id, gmPage, gmPageSize);
    } catch (err) { setError(err.message); }
    setAddingMembers(false);
  };

  const handleRemoveFromGroup = async (userId) => {
    if (!gmGroup) return;
    setRemoveGmLoading(true); setError('');
    try {
      await apiCallNoBody(`${base}/groups/${gmGroup.id}/members/${userId}`, { method: 'DELETE' });
      setRemoveGmId(null);
      await fetchGroupMembers(gmGroup.id, gmPage, gmPageSize);
    } catch (err) { setError(err.message); }
    setRemoveGmLoading(false);
  };

  // Group member multi-select helpers
  const gmPageIds = gmMembers.map((m) => m.user_id || m.id);
  const gmAllSelected = gmPageIds.length > 0 && gmPageIds.every((id) => selectedGmIds.has(id));
  const gmSomeSelected = gmPageIds.some((id) => selectedGmIds.has(id));

  const toggleGmSelect = (id) => {
    setSelectedGmIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const toggleGmSelectAll = () => {
    setSelectedGmIds((prev) => { const next = new Set(prev); if (gmAllSelected) gmPageIds.forEach((id) => next.delete(id)); else gmPageIds.forEach((id) => next.add(id)); return next; });
  };

  const handleBulkRemoveGm = async () => {
    if (!gmGroup) return;
    setBulkRemoveGmLoading(true); setError('');
    try {
      const result = await apiCall(`${base}/groups/${gmGroup.id}/members/bulk`, {
        method: 'DELETE',
        body: JSON.stringify({ user_ids: [...selectedGmIds] }),
      });
      if (result?.not_found_count > 0) {
        setError(`${result.removed_count} removed, ${result.not_found_count} not found.`);
      }
      setSelectedGmIds(new Set());
      setBulkRemoveGmConfirm(false);
      await fetchGroupMembers(gmGroup.id, gmPage, gmPageSize);
    } catch (err) { setError(err.message); }
    setBulkRemoveGmLoading(false);
  };

  // ======= RENDER =======

  return (
    <>
      {error && <div className="error-message">{error}</div>}

      <div className="orgs-section-header" style={{ marginBottom: '1rem' }}>
        <button onClick={openCreate} className="btn btn-primary" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
          <AddIcon sx={{ fontSize: '0.875rem' }} /> Add Group
        </button>
        <span className="pagination-info">{total} group(s)</span>
      </div>

      <div className="search-bar" style={{ marginBottom: '0.75rem' }}>
        <div className="search-input-wrapper">
          <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
          <input type="text" placeholder="Search by group name..." value={groupsSearch} onChange={(e) => setGroupsSearch(e.target.value)} className="search-input" />
          {groupsSearch && <button onClick={() => setGroupsSearch('')} className="clear-search-btn"><CloseIcon sx={{ fontSize: '1rem' }} /></button>}
        </div>
      </div>

      {loading ? <Loader size="small" /> : (
        <div className="table-card">
          <div className="users-table-wrapper">
            <table className="users-table">
              <thead><tr><th>Group ID</th><th>Name</th><th>Members</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr><td colSpan={5} className="no-results">
                    {debouncedGroupsSearch.trim() ? `No results for "${debouncedGroupsSearch.trim()}"` : 'No groups found'}
                  </td></tr>
                ) : groups.map((g) => (
                  <tr key={g.id} className="user-row">
                    <td className="td-id" title={g.id}>{g.id || '—'}</td>
                    <td>{g.name || '—'}</td>
                    <td className="td-storage">{g.member_count != null ? g.member_count : '—'}</td>
                    <td className="td-date">{formatDate(g.created_at)}</td>
                    <td className="td-actions">
                      <button className="btn-icon-sm" title="Manage members" onClick={() => openGroupMembers(g)}><GroupIcon sx={{ fontSize: '1.125rem' }} /></button>
                      <button className="btn-icon-sm btn-shield" title="Edit" onClick={() => openEdit(g)}><EditIcon sx={{ fontSize: '1.125rem' }} /></button>
                      <button className="btn-icon-sm btn-delete" title="Delete" onClick={() => setDeleteId(g.id)}><DeleteOutlineIcon sx={{ fontSize: '1.125rem' }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 0 && <PageFooter page={page} pageSize={pageSize} total={total} totalPages={totalPages} onPageChange={setPage} onPageSizeChange={setPageSize} />}
        </div>
      )}

      {/* Create/Edit Group Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">{editGroup ? 'Edit Group' : 'Create Group'}</h3><button onClick={closeModal} className="btn-close" disabled={saving}><CloseIcon sx={{ fontSize: '1.25rem' }} /></button></div>
            <div className="modal-body"><div className="form-group"><label className="form-label">Group Name</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" placeholder="1–255 characters" maxLength={255} /></div></div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? 'Saving...' : editGroup ? 'Save' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Confirm */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => !deleteLoading && setDeleteId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Delete Group</h3></div>
            <div className="modal-body"><p className="confirm-text">Delete group <strong className="user-id-highlight">{deleteId}</strong>?</p><p className="confirm-subtext">This action cannot be undone.</p></div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setDeleteId(null)} disabled={deleteLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteId)} disabled={deleteLoading}>{deleteLoading ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Group Members Modal — two sections: current members + add from org */}
      {gmGroup && (
        <div className="modal-overlay" onClick={() => !addingMembers && !removeGmLoading && closeGroupMembers()}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '56rem', maxHeight: '85vh' }}>
            <div className="modal-header">
              <div><h3 className="modal-title">Manage Group Members</h3><p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: '#9ca3af' }}>{gmGroup.name}</p></div>
              <button onClick={closeGroupMembers} className="btn-close"><CloseIcon sx={{ fontSize: '1.25rem' }} /></button>
            </div>
            <div className="modal-body" style={{ overflow: 'auto', maxHeight: 'calc(85vh - 5rem)' }}>

              {/* Current group members */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ color: 'white', fontSize: '0.9375rem', margin: 0 }}>Current Members ({gmTotal})</h4>
                {selectedGmIds.size > 0 && (
                  <button onClick={() => setBulkRemoveGmConfirm(true)} className="btn btn-danger" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                    <DeleteSweepIcon sx={{ fontSize: '0.875rem' }} /> Remove Selected ({selectedGmIds.size})
                  </button>
                )}
              </div>
              <div className="search-bar" style={{ marginBottom: '0.5rem' }}>
                <div className="search-input-wrapper">
                  <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
                  <input type="text" placeholder="Search by email or name..." value={gmSearch} onChange={(e) => setGmSearch(e.target.value)} className="search-input" />
                  {gmSearch && <button onClick={() => setGmSearch('')} className="clear-search-btn"><CloseIcon sx={{ fontSize: '1rem' }} /></button>}
                </div>
              </div>
              {gmLoading ? <Loader size="small" /> : gmMembers.length === 0 ? (
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.5rem' }}>
                  {debouncedGmSearch.trim() ? `No results for "${debouncedGmSearch.trim()}"` : 'No members in this group.'}
                </p>
              ) : (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div className="users-table-wrapper">
                    <table className="users-table">
                      <thead><tr>
                        <th className="th-checkbox"><input type="checkbox" checked={gmAllSelected} ref={(el) => { if (el) el.indeterminate = gmSomeSelected && !gmAllSelected; }} onChange={toggleGmSelectAll} /></th>
                        <th>User ID</th><th>Email</th><th>Name</th><th>Added</th><th>Actions</th>
                      </tr></thead>
                      <tbody>
                        {gmMembers.map((gm) => {
                          const uid = gm.user_id || gm.id;
                          return (
                            <tr key={uid} className="user-row">
                              <td className="td-checkbox"><input type="checkbox" checked={selectedGmIds.has(uid)} onChange={() => toggleGmSelect(uid)} /></td>
                              <td className="td-id" title={uid}>{uid || '—'}</td>
                              <td>{gm.email || '—'}</td>
                              <td>{gm.display_name || '—'}</td>
                              <td className="td-date">{formatDate(gm.created_at)}</td>
                              <td className="td-actions">
                                {removeGmId === uid ? (
                                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                                    <button className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => handleRemoveFromGroup(uid)} disabled={removeGmLoading}>{removeGmLoading ? '...' : 'Confirm'}</button>
                                    <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => setRemoveGmId(null)} disabled={removeGmLoading}>No</button>
                                  </div>
                                ) : (
                                  <button className="btn-icon-sm btn-delete" title="Remove from group" onClick={() => setRemoveGmId(uid)}><PersonRemoveIcon sx={{ fontSize: '1.125rem' }} /></button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {gmTotalPages > 1 && <PageFooter page={gmPage} pageSize={gmPageSize} total={gmTotal} totalPages={gmTotalPages} onPageChange={setGmPage} onPageSizeChange={setGmPageSize} />}
                </div>
              )}

              {/* Add org members to group */}
              <div style={{ borderTop: '1px solid #1a1a2e', paddingTop: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h4 style={{ color: 'white', fontSize: '0.9375rem', margin: 0 }}>
                    <PersonAddIcon sx={{ fontSize: '1rem', verticalAlign: 'text-bottom', mr: 0.5 }} /> Add Members from Organization
                  </h4>
                  {selectedToAdd.size > 0 && (
                    <button className="btn btn-primary" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }} onClick={handleAddSelectedToGroup} disabled={addingMembers}>
                      {addingMembers ? 'Adding...' : `Add Selected (${selectedToAdd.size})`}
                    </button>
                  )}
                </div>

                <div className="search-bar" style={{ marginBottom: '0.5rem' }}>
                  <div className="search-input-wrapper">
                    <SearchIcon className="search-icon" sx={{ fontSize: '1.25rem' }} />
                    <input type="text" placeholder="Search by email or name..." value={omSearch} onChange={(e) => setOmSearch(e.target.value)} className="search-input" />
                    {omSearch && <button onClick={() => setOmSearch('')} className="clear-search-btn"><CloseIcon sx={{ fontSize: '1rem' }} /></button>}
                  </div>
                </div>

                {omLoading ? <Loader size="small" /> : (
                  <>
                    <div className="users-table-wrapper">
                      <table className="users-table">
                        <thead><tr><th className="th-checkbox" style={{ width: '2.5rem' }}></th><th>Email</th><th>Name</th><th>Status</th></tr></thead>
                        <tbody>
                          {orgMembers.length === 0 ? (
                            <tr><td colSpan={4} className="no-results">
                              {debouncedOmSearch.trim() ? `No results for "${debouncedOmSearch.trim()}"` : 'No members found'}
                            </td></tr>
                          ) : orgMembers.map((m) => {
                            const uid = m.user_id || m.id;
                            const alreadyInGroup = gmMemberIds.has(uid);
                            return (
                              <tr key={uid} className="user-row" style={alreadyInGroup ? { opacity: 0.5 } : {}}>
                                <td className="td-checkbox">
                                  <input type="checkbox" checked={selectedToAdd.has(uid)} onChange={() => toggleAddSelect(uid)} disabled={alreadyInGroup} style={alreadyInGroup ? { cursor: 'not-allowed' } : {}} />
                                </td>
                                <td>{m.email || '—'}</td>
                                <td>{m.display_name || '—'}</td>
                                <td>{alreadyInGroup ? <span className="badge badge-gray">In group</span> : <span className="badge badge-blue">Available</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {omTotalPages > 1 && <PageFooter page={omPage} pageSize={omPageSize} total={omTotal} totalPages={omTotalPages} onPageChange={setOmPage} onPageSizeChange={setOmPageSize} />}
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Bulk Remove Group Members Confirm */}
      {bulkRemoveGmConfirm && (
        <div className="modal-overlay" onClick={() => !bulkRemoveGmLoading && setBulkRemoveGmConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3 className="modal-title">Remove Members from Group</h3></div>
            <div className="modal-body">
              <p className="confirm-text">Remove <strong className="user-id-highlight">{selectedGmIds.size}</strong> selected member(s) from this group?</p>
              <p className="confirm-subtext">This action cannot be undone.</p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button className="btn btn-secondary" onClick={() => setBulkRemoveGmConfirm(false)} disabled={bulkRemoveGmLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleBulkRemoveGm} disabled={bulkRemoveGmLoading}>{bulkRemoveGmLoading ? 'Removing...' : `Remove ${selectedGmIds.size} Member(s)`}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GroupsTab;
